import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

// URL regex: matches http(s) URLs and bare domain-like patterns (word boundary to avoid false positives like "file.txt")
const URL_REGEX = /https?:\/\/[^\s),]+|\b[\w-]+(?:\.[\w-]+)+\.(?:com|net|org|co|io|test|example|dev|app|xyz|info)(?:\/[^\s),]*)*/gi;

interface LinkWarningModalProps {
  isOpen: boolean;
  title: string;
  body: string;
  url: string;
  onClose: () => void;
}

function LinkWarningModal({ isOpen, title, body, url, onClose }: LinkWarningModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="link-warning-title">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Modal */}
      <div className="relative w-full max-w-[280px] overflow-hidden rounded-2xl border border-white/20 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-[#2C2C2E]/95">
        {/* Warning icon */}
        <div className="flex flex-col items-center px-5 pt-5 pb-2">
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 dark:text-amber-400">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>
          <h3 id="link-warning-title" className="text-center text-[15px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
            {title}
          </h3>
        </div>
        <div className="px-5 pb-3">
          <p className="text-center text-[13px] leading-snug text-[#3C3C43]/80 dark:text-[#EBEBF5]/70">
            {body}
          </p>
          <div className="mt-2 rounded-lg bg-black/5 px-3 py-1.5 dark:bg-white/5">
            <p className="break-all text-center text-[11px] font-mono text-red-600 dark:text-red-400">
              {url}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full border-t border-black/10 py-3 text-center text-[15px] font-medium text-blue-500 transition-colors hover:bg-black/5 dark:border-white/10 dark:text-blue-400 dark:hover:bg-white/5"
        >
          OK
        </button>
      </div>
    </div>,
    document.body
  );
}

interface ChatMessageContentProps {
  content: string;
  linkWarningTitle?: string;
  linkWarningBody?: string;
  onLinkClick?: (url: string) => boolean | void;
}

export function ChatMessageContent({ content, linkWarningTitle, linkWarningBody, onLinkClick }: ChatMessageContentProps) {
  const [warningUrl, setWarningUrl] = useState<string | null>(null);

  const handleLinkClick = useCallback((url: string) => {
    const handled = onLinkClick?.(url);
    if (!handled && linkWarningTitle) {
      setWarningUrl(url);
    }
  }, [onLinkClick, linkWarningTitle]);

  // If no link handling configured, render as plain text (backward compatible)
  if (!linkWarningTitle && !onLinkClick) {
    return <>{content}</>;
  }

  // Split content into text and URL segments
  const parts: Array<{ type: "text" | "url"; value: string }> = [];
  let lastIndex = 0;

  for (const match of content.matchAll(URL_REGEX)) {
    const matchIndex = match.index!;
    if (matchIndex > lastIndex) {
      parts.push({ type: "text", value: content.slice(lastIndex, matchIndex) });
    }
    parts.push({ type: "url", value: match[0] });
    lastIndex = matchIndex + match[0].length;
  }
  if (lastIndex < content.length) {
    parts.push({ type: "text", value: content.slice(lastIndex) });
  }

  // If no URLs found, render as plain text
  if (parts.length === 0 || parts.every(p => p.type === "text")) {
    return <>{content}</>;
  }

  return (
    <>
      {parts.map((part, i) =>
        part.type === "url" ? (
          <button
            key={i}
            type="button"
            onClick={() => handleLinkClick(part.value)}
            className="inline text-left text-blue-600 underline decoration-blue-400/50 underline-offset-2 transition-colors hover:text-blue-700 hover:decoration-blue-500 dark:text-blue-400 dark:decoration-blue-500/50 dark:hover:text-blue-300"
          >
            {part.value}
          </button>
        ) : (
          <span key={i}>{part.value}</span>
        )
      )}
      <LinkWarningModal
        isOpen={warningUrl !== null}
        title={linkWarningTitle || ""}
        body={linkWarningBody || ""}
        url={warningUrl || ""}
        onClose={() => setWarningUrl(null)}
      />
    </>
  );
}
